/**
 * Snapshot de los datos locales del usuario activo (invitado o logueado) y su
 * importación transaccional e idempotente en el dispositivo receptor.
 *
 * - El snapshot son las FILAS MATERIALIZADAS de las tablas de dominio del
 *   usuario (`USER_DATA_TABLES`). No hace falta transferir la cola `ps_crud`:
 *   al insertar por la API de PowerSync, el receptor genera sus PROPIAS
 *   operaciones de subida equivalentes (filas completas), así que los cambios
 *   pendientes del emisor viajan ya materializados en sus filas.
 * - Importación: se re-estampa `user_id` al usuario del receptor (mismo patrón
 *   que `sweepVisitorData`) y solo se insertan filas cuyo `id` no exista →
 *   re-importar es un no-op (idempotencia por id).
 * - Seguridad: el snapshot llega de fuera → tablas SOLO de la allowlist y
 *   columnas validadas contra un patrón de identificador antes de interpolar.
 */

import { USER_DATA_TABLES } from "@fieldcheck/powersync";
import { isRecord } from "./guards";

/** Superficie mínima de BD (PowerSync la cumple; los tests inyectan una fake). */
export interface SnapshotLockContext {
  getAll<T>(sql: string, params?: unknown[]): Promise<T[]>;
  execute(sql: string, params?: unknown[]): Promise<unknown>;
}

export interface SnapshotDb extends SnapshotLockContext {
  writeTransaction<T>(fn: (tx: SnapshotLockContext) => Promise<T>): Promise<T>;
}

export const SNAPSHOT_VERSION = 1;

export interface TransferSnapshot {
  version: number;
  tables: Record<string, Record<string, unknown>[]>;
}

export interface ImportCounts {
  inserted: number;
  skipped: number;
}

const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/u;

export async function buildSnapshot(
  db: SnapshotLockContext,
  userId: string,
  tables: readonly string[] = USER_DATA_TABLES,
): Promise<TransferSnapshot> {
  const snapshot: TransferSnapshot = { version: SNAPSHOT_VERSION, tables: {} };
  for (const table of tables) {
    if (!IDENTIFIER_RE.test(table)) {
      throw new Error(`Invalid table name: ${table}`);
    }
    snapshot.tables[table] = await db.getAll<Record<string, unknown>>(
      `SELECT * FROM ${table} WHERE user_id = ?`,
      [userId],
    );
  }
  return snapshot;
}

export function snapshotRowCount(snapshot: TransferSnapshot): number {
  return Object.values(snapshot.tables).reduce((sum, rows) => sum + rows.length, 0);
}

export function parseSnapshotJson(json: string): TransferSnapshot | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.version !== SNAPSHOT_VERSION || !isRecord(parsed.tables)) {
    return null;
  }
  // Reconstruimos el snapshot validando cada fila (JSON no confiable):
  // sin aserciones de tipo, solo lo que pasa los guards entra.
  const tables: Record<string, Record<string, unknown>[]> = {};
  for (const [name, rows] of Object.entries(parsed.tables)) {
    if (!Array.isArray(rows)) {
      return null;
    }
    const validRows: Record<string, unknown>[] = [];
    const rowList: unknown[] = rows;
    for (const row of rowList) {
      if (!isRecord(row)) {
        return null;
      }
      validRows.push(row);
    }
    tables[name] = validRows;
  }
  return { version: SNAPSHOT_VERSION, tables };
}

/**
 * Importa el snapshot en una única transacción de escritura.
 *
 * Idempotente: las filas cuyo `id` ya existe localmente se omiten (nunca se
 * sobrescribe lo local). Las importadas se re-estampan con `receiverUserId`,
 * de modo que (a) la UI local las muestra al usuario activo y (b) si el
 * receptor está logueado, la cola de PowerSync las sube con el dueño correcto.
 */
export async function importSnapshot(
  db: SnapshotDb,
  snapshot: TransferSnapshot,
  receiverUserId: string,
  allowedTables: readonly string[] = USER_DATA_TABLES,
): Promise<ImportCounts> {
  if (!receiverUserId) {
    throw new Error("receiverUserId is required");
  }
  const counts: ImportCounts = { inserted: 0, skipped: 0 };

  await db.writeTransaction(async (tx) => {
    // Orden de la allowlist (padres antes que hijos), no el del snapshot.
    for (const table of allowedTables) {
      const rows = snapshot.tables[table];
      if (!rows?.length) {
        continue;
      }

      // Desfase de versión emisor/receptor: solo insertamos columnas que
      // existan localmente (PRAGMA funciona también sobre las vistas de
      // PowerSync). Sin filas o sin pragma → sin filtro extra.
      const tableInfoRows = await tx.getAll<{ name: string }>(`PRAGMA table_info(${table})`);
      const localColumns = new Set(tableInfoRows.map((c) => c.name));

      const incomingIds = rows
        .map((row) => row.id)
        .filter((id): id is string => typeof id === "string" && id.length > 0);
      const existing = new Set<string>();
      const BATCH = 200;
      for (let i = 0; i < incomingIds.length; i += BATCH) {
        const batch = incomingIds.slice(i, i + BATCH);
        const placeholders = batch.map(() => "?").join(",");
        const found = await tx.getAll<{ id: string }>(
          `SELECT id FROM ${table} WHERE id IN (${placeholders})`,
          batch,
        );
        for (const row of found) {
          existing.add(row.id);
        }
      }

      for (const row of rows) {
        const { id } = row;
        if (typeof id !== "string" || id.length === 0) {
          counts.skipped += 1;
          continue;
        }
        if (existing.has(id)) {
          counts.skipped += 1;
          continue;
        }
        const stamped: Record<string, unknown> = { ...row };
        if ("user_id" in stamped) {
          stamped.user_id = receiverUserId;
        }
        const columns = Object.keys(stamped).filter(
          (c) => IDENTIFIER_RE.test(c) && (localColumns.size === 0 || localColumns.has(c)),
        );
        if (columns.length === 0) {
          counts.skipped += 1;
          continue;
        }
        const placeholders = columns.map(() => "?").join(", ");
        await tx.execute(
          `INSERT INTO ${table} (${columns.join(", ")}) VALUES (${placeholders})`,
          columns.map((c) => stamped[c] ?? null),
        );
        counts.inserted += 1;
      }
    }
  });

  return counts;
}
